import { Text, Grid } from "@chakra-ui/react";
import { AmortizationViewModel, RateEntry } from "@common/loans/statements/amortization.view-model";
import { useCustomFormats } from "hooks/useCustomFormat";
import moment from "moment";
import React from "react";

type InterestItem = {
  description: string;
  rate: string;
};

export function Interests({ statement }: { statement: AmortizationViewModel }) {
  const { formatDate } = useCustomFormats();

  const getRateRangeDescription = (
    type: "base rate" | "default interest",
    item: RateEntry
  ): string => {
    if (item.endAt === "current") {
      return `Effective ${type} since ${formatDate(item.startAt)}`;
    }

    return `Effective ${type} from ${formatDate(item.startAt)} to ${formatDate(item.endAt)}`;
  };

  const interestRateList: InterestItem[] = statement.interestRates
    .sort((a, b) => (moment(a.startAt).isBefore(moment(b.startAt)) ? -1 : 1))
    .map(item => ({
      description: "Fixed Interest",
      rate: `${item.rate}% per year`,
    }));

  const baseRateList: InterestItem[] = statement.baseRates
    .sort((a, b) => (moment(a.startAt).isBefore(moment(b.startAt)) ? -1 : 1))
    .map(item => ({
      description: getRateRangeDescription("base rate", item),
      rate: item.rate.toFixed(2) + "% per year",
    }));

  const defaultInterestList: InterestItem[] = statement.defaultInterests
    .sort((a, b) => (moment(a.startAt).isBefore(moment(b.startAt)) ? -1 : 1))
    .map(item => ({
      description: getRateRangeDescription("default interest", item),
      rate: `${item.rate.toFixed(2)}% per month`,
    }));

  const renderInterestList = (list: InterestItem[]) => {
    if (list.length === 0) return null;

    return list.map(item => (
      <React.Fragment key={`${item.description}-${item.rate}`}>
        <Text color="black" fontSize="16px">
          {item.description}
        </Text>
        <Text color="black" fontSize="16px">
          {item.rate}
        </Text>
      </React.Fragment>
    ));
  };

  return (
    <Grid templateColumns="auto 150px" rowGap={0} columnGap={4} width="fit-content">
      {renderInterestList(interestRateList)}
      {renderInterestList(baseRateList)}
      {renderInterestList(defaultInterestList)}
    </Grid>
  );
}
